import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzTableModule } from 'ng-zorro-antd/table';
import { FlexModule } from '@angular/flex-layout';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { MatIconModule } from '@angular/material/icon';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { IImagingDetailed } from 'src/app/interfaces/Imaging/IImagingDetailed';
import { ImagingService } from 'src/app/services/imaging.service';
import { Subject, takeUntil } from 'rxjs';
import { BrowserModule } from '@angular/platform-browser';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzImageModule } from 'ng-zorro-antd/image';
import { CommonModule } from '@angular/common';
import { NzSelectModule, NzSelectOptionInterface } from 'ng-zorro-antd/select';
import { IMSPrediction } from 'src/app/interfaces/Imaging/IMSPrediction';
import { SwalToastService } from 'src/app/services/swal.service';
import { PermissionService } from 'src/app/services/permission.service';
import { PermissionModule } from 'src/app/modules/permission.module';
import { date_format_html } from 'src/date/date.configuration';

@Component({
  selector: 'app-precious-med-sam-segmentation',
  standalone: true,
  imports: [
    NzLayoutModule,
    NzTableModule,
    TranslateModule,
    CommonModule,
    FlexModule,
    NzTooltipModule,
    NzIconModule,
    MatIconModule,
    NzSwitchModule,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzPaginationModule,
    BrowserModule,
    PdfViewerModule,
    NzSpinModule,
    NzImageModule,
    NzSelectModule,
    PermissionModule,
  ],
  templateUrl: './precious-med-sam-segmentation-component.component.html',
  styleUrl: './precious-med-sam-segmentation-component.component.css',
})
export class PreciousMedSamSegmentationComponentComponent implements OnDestroy {
  /* =========================
     Template references
     ========================= */
  @ViewChild('imgEl') imgEl!: ElementRef<HTMLImageElement>;
  @ViewChild('overlayEl') overlayEl!: ElementRef<HTMLDivElement>;

  @Input() imagingRecords: NzSelectOptionInterface[] = [];

  /* =========================
     Internal state
     ========================= */
  private destroy$ = new Subject<void>();
  private imagingDataRaw: IImagingDetailed[] = [];

  selectedImaging?: number;
  imageSrc?: string;

  isLoading = false;
  result?: any;

  /* =========================
     Bounding box state
     ========================= */
  private isDrawing = false;
  private startX = 0;
  private startY = 0;

  drawRectVisible = false;

  drawRect = {
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  };

  /** Final MedSAM box (IMAGE PIXELS) */
  box?: { x1: number; y1: number; x2: number; y2: number };

  constructor(
    private imagingService: ImagingService,
    private permissionService: PermissionService,
    private swalToastService: SwalToastService,
  ) {}

  /* =========================
     Lifecycle
     ========================= */
  ngOnInit() {
    this.imagingService.imagingLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => (this.isLoading = v));

    this.imagingService.imagingDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IImagingDetailed[]) => {
        this.imagingDataRaw = data;

        this.imagingRecords =
          data
            .filter((i) => i.categoryId === 8 && i.modalityId === 6)
            .map((i) => ({
              label: `${i.examTitle} ${i.date}`,
              value: i.id,
            })) || [];
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* =========================
     Image loading
     ========================= */
  onImagingSelected(imagingId: number | null) {
    this.resetState();

    if (!imagingId) return;

    const imaging = this.imagingDataRaw.find((i) => i.id === imagingId);
    const imagePath = imaging?.paths?.[0];

    if (!imagePath) {
      this.swalToastService.toastError('No image file found');
      return;
    }

    this.isLoading = true;

    this.imagingService
      .preview(imagePath.id.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            this.imageSrc = reader.result as string;
            this.isLoading = false;
          };
          reader.readAsDataURL(blob);
        },
        error: () => {
          this.isLoading = false;
          this.swalToastService.toastError('Unable to load image');
        },
      });
  }

  onImageLoaded() {
    // Reset box when new image is loaded
    this.box = undefined;
    this.drawRectVisible = false;
  }

  /* =========================
     Pointer-based bbox logic
     (pixel-accurate)
     ========================= */
  onPointerDown(evt: PointerEvent) {
    if (!this.imgEl || !this.overlayEl) return;

    const rect = this.overlayEl.nativeElement.getBoundingClientRect();

    this.isDrawing = true;
    this.drawRectVisible = true;

    this.startX = evt.clientX - rect.left;
    this.startY = evt.clientY - rect.top;

    this.drawRect.left = this.startX;
    this.drawRect.top = this.startY;
    this.drawRect.width = 0;
    this.drawRect.height = 0;
  }

  onPointerMove(evt: PointerEvent) {
    if (!this.isDrawing) return;

    const rect = this.overlayEl.nativeElement.getBoundingClientRect();

    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;

    this.drawRect.left = Math.min(this.startX, x);
    this.drawRect.top = Math.min(this.startY, y);
    this.drawRect.width = Math.abs(x - this.startX);
    this.drawRect.height = Math.abs(y - this.startY);
  }

  onPointerUp(evt: PointerEvent) {
    if (!this.isDrawing) return;
    this.isDrawing = false;

    if (!this.imgEl) return;

    const img = this.imgEl.nativeElement;

    const imgRect = img.getBoundingClientRect();
    const overlayRect = this.overlayEl.nativeElement.getBoundingClientRect();

    // Scale factors (DISPLAY → IMAGE)
    const scaleX = img.naturalWidth / imgRect.width;
    const scaleY = img.naturalHeight / imgRect.height;

    // Convert overlay coords → image coords
    const x1 =
      (this.drawRect.left - (imgRect.left - overlayRect.left)) * scaleX;
    const y1 = (this.drawRect.top - (imgRect.top - overlayRect.top)) * scaleY;
    const x2 = x1 + this.drawRect.width * scaleX;
    const y2 = y1 + this.drawRect.height * scaleY;

    // Minimum size guard
    if (Math.abs(x2 - x1) < 5 || Math.abs(y2 - y1) < 5) {
      this.box = undefined;
      this.drawRectVisible = false;
      return;
    }

    this.box = {
      x1: Math.round(Math.min(x1, x2)),
      y1: Math.round(Math.min(y1, y2)),
      x2: Math.round(Math.max(x1, x2)),
      y2: Math.round(Math.max(y1, y2)),
    };
  }

  /* =========================
     Segmentation call
     ========================= */
  segment() {
    if (!this.selectedImaging || !this.box) return;

    this.result = undefined;
    this.isLoading = true;

    this.imagingService
      .segmentPreciousMedSam(
        this.selectedImaging,
        this.box.x1,
        this.box.y1,
        this.box.x2,
        this.box.y2,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.result = res.data?.results?.[0];
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  /* =========================
     Helpers
     ========================= */
  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  private resetState() {
    this.imageSrc = undefined;
    this.result = undefined;
    this.box = undefined;
    this.drawRectVisible = false;
  }
}
